import z from "zod";

/**
 * The response sets this instrument family draws on.
 *
 * One set per distinct list of valid values, and that is the whole rule: "0–3"
 * and "0–3 or unknown" are different sets, because a document valid against one
 * is not valid against the other. Sharing a set between two items is what makes
 * "these are scored on the same scale" a fact rather than the same four numbers
 * appearing twice, and it is what an aggregate hangs from.
 *
 * A number is a score an aggregate may sum; a string is a fact it may not. The
 * JSON type is the whole of that distinction, so nothing annotates an option to
 * say which it is.
 *
 * Shared across the versions of this family rather than restated in each,
 * because a set is the family's vocabulary. It is not a reference to a previous
 * version: a version's module says what that version asks, without reading what
 * the one before it asked.
 *
 * An id is a namespace, a version of that namespace, and the word the set is
 * called by. The version is there so adding a level to a set is a second set
 * named beside the first rather than a worse spelling of the same word, and the
 * namespace is there so two owners can each have an `action_level`. The
 * separator is a dot because the id is emitted into a `$ref` verbatim, and a
 * slash in it would read as another step down the pointer.
 */

/** How much is needed here: none, watchful, action, immediate action. */
export const actionLevel = z
  .literal([0, 1, 2, 3])
  .meta({ id: "thalamos.v1.action_level" });

/** The same four levels, where the assessor could not determine one. */
export const actionLevelOrUnknown = z
  .literal([0, 1, 2, 3, "unknown"])
  .meta({ id: "thalamos.v1.action_level_or_unknown" });

/** The same four levels, where the item does not apply to this young person. */
export const actionLevelOrNotApplicable = z
  .literal([0, 1, 2, 3, "not_applicable"])
  .meta({ id: "thalamos.v1.action_level_or_not_applicable" });

/** A plain fact, where a level would say more than is known. */
export const yesNo = z
  .literal(["yes", "no"])
  .meta({ id: "thalamos.v1.yes_no" });
